'use client'

import React, { useState, useEffect } from 'react'
import { Tab } from '@headlessui/react'
import { StrategyDashboardData } from '@/types'
import { 
  BookOpen, 
  Settings, 
  BarChart3, 
  FileText, 
  Activity,
  Calendar,
  TrendingUp,
  AlertCircle,
  Target,
  Edit,
  Play,
  Trash2
} from 'lucide-react'
import StrategyBasicInfo from './StrategyBasicInfo'
import StrategyLogic from './StrategyLogic'
import ParameterConfig from './ParameterConfig'
import BacktestResults from './BacktestResults'
import ResearchNotes from './ResearchNotes'
import LiveTracking from './LiveTracking'
import ManualTestTrades from './ManualTestTrades'
import EditStrategyModal from './EditStrategyModal'
import EditStrategyLogicModal from './EditStrategyLogicModal'
import ConfirmDialog from './ConfirmDialog'
import Toast from './Toast'
import { Link, useNavigate } from 'react-router-dom'
import { getStrategyDetail, deleteStrategy } from '../lib/api'

interface StrategyDetailProps {
  strategyId: string
}

const StrategyDetail: React.FC<StrategyDetailProps> = ({ strategyId }) => {
  const navigate = useNavigate()
  const [data, setData] = useState<StrategyDashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showEditStrategyModal, setShowEditStrategyModal] = useState(false)
  const [showEditLogicModal, setShowEditLogicModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  useEffect(() => {
    fetchStrategyData()
  }, [strategyId])

  const fetchStrategyData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const result = await getStrategyDetail(strategyId)
      setData(result)
    } catch (err) {
      console.error('Error fetching strategy data:', err)
      setError(err instanceof Error ? err.message : '加载策略数据失败')
    } finally {
      setLoading(false)
    }
  }

  const handleEditStrategySuccess = () => {
    fetchStrategyData()
  }

  const handleEditLogicSuccess = () => {
    fetchStrategyData()
  }

  const handleDeleteStrategy = async () => {
    try {
      setDeleting(true)
      await deleteStrategy(strategyId)
      setToast({ message: '策略已成功删除', type: 'success' })
      setTimeout(() => {
        navigate('/strategies')
      }, 1500)
    } catch (error) {
      console.error('Error deleting strategy:', error)
      setToast({ 
        message: error instanceof Error ? error.message : '删除策略失败', 
        type: 'error' 
      })
    } finally {
      setDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const tabs = [
    {
      name: '基本信息',
      icon: BookOpen,
      component: <StrategyBasicInfo strategy={data?.strategy} onEditClick={() => setShowEditStrategyModal(true)} />
    },
    {
      name: '策略逻辑',
      icon: FileText,
      component: <StrategyLogic strategy={data?.strategy} onEditClick={() => setShowEditLogicModal(true)} />
    },
    {
      name: '参数配置',
      icon: Settings,
      component: <ParameterConfig parameters={data?.parameters || []} strategyId={strategyId} onUpdate={fetchStrategyData} />
    },
    {
      name: '回测结果',
      icon: BarChart3,
      component: <BacktestResults strategyId={strategyId} />
    },
    {
      name: '手动测试',
      icon: Target,
      component: (
        <div>
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">手动测试交易记录</h3>
            <Link
              to={`/strategy/${strategyId}/manual-test`}
              className="btn-primary flex items-center whitespace-nowrap"
            >
              <Target className="w-4 h-4 mr-2" />
              查看详细管理页面
            </Link>
          </div>
          <ManualTestTrades strategyId={strategyId} />
        </div>
      )
    },
    {
      name: '研究笔记',
      icon: FileText,
      component: <ResearchNotes strategyId={strategyId} latestNote={data?.latest_note} />
    },
    {
      name: '实盘追踪',
      icon: Activity,
      component: <LiveTracking strategyId={strategyId} />
    }
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-violet-400"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="card text-center">
          <AlertCircle className="mx-auto h-16 w-16 text-red-400 mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">加载失败</h3>
          <p className="text-white/70 mb-6">{error}</p>
          <button
            onClick={fetchStrategyData}
            className="btn-primary flex items-center whitespace-nowrap"
          >
            重试
          </button>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="card text-center">
          <h3 className="text-xl font-medium text-white mb-2">未找到策略</h3>
          <p className="text-white/70">请检查策略ID是否正确</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Tab内容 */}
      <div className="max-w-7xl mx-auto">
        <Tab.Group>
          {/* 页面头部 */}
          <div className="card mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gradient mb-2">
                  {data.strategy.name}
                </h1>
                <div className="flex items-center space-x-4 text-sm text-white/60">
                  <span className="flex items-center">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    {data.strategy.type}
                  </span>
                  <span className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    创建于 {new Date(data.strategy.created_at).toLocaleDateString('zh-CN')}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    data.strategy.status === 'live' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                    data.strategy.status === 'test' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                    'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                  }`}>
                    {data.strategy.status === 'live' ? '实盘中' :
                     data.strategy.status === 'test' ? '验证中' : '草稿'}
                  </span>
                </div>
              </div>
              <div className="flex space-x-2 sm:space-x-3">
                <button 
                  className="btn-secondary flex items-center justify-center whitespace-nowrap"
                  title="编辑策略"
                  onClick={() => setShowEditStrategyModal(true)}
                >
                  <Edit className="w-4 h-4 mr-2 sm:mr-2" />
                  <span className="hidden sm:inline">编辑策略</span>
                </button>
                <button 
                  className="btn-primary flex items-center justify-center whitespace-nowrap"
                  title="新建回测"
                  onClick={() => {
                    // 切换到回测结果标签页并显示添加表单
                    const backtestTab = document.querySelector('[data-tab="回测结果"]') as HTMLElement
                    if (backtestTab) {
                      backtestTab.click()
                      // 延迟一下让标签页切换完成
                      setTimeout(() => {
                        const addButton = document.querySelector('[data-add-backtest]') as HTMLElement
                        if (addButton) {
                          addButton.click()
                        }
                      }, 100)
                    }
                  }}
                >
                  <Play className="w-4 h-4 mr-2 sm:mr-2" />
                  <span className="hidden sm:inline">新建回测</span>
                </button>
                <button 
                  className="btn-danger flex items-center justify-center whitespace-nowrap"
                  title="删除策略"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleting}
                >
                  <Trash2 className="w-4 h-4 mr-2 sm:mr-2" />
                  <span className="hidden sm:inline">{deleting ? '删除中...' : '删除策略'}</span>
                </button>
              </div>
            </div>
          </div>

          <Tab.List className="card mb-6">
            <div className="flex space-x-1">
              {tabs.map((tab) => (
                <Tab
                  key={tab.name}
                  data-tab={tab.name}
                  className={({ selected }) =>
                    `flex-1 rounded-xl py-3 text-sm font-medium leading-5 transition-all duration-200
                     ${selected
                      ? 'bg-violet-500/20 text-white border border-violet-500/30 shadow-lg'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                     }`
                  }
                >
                  <div className="flex items-center justify-center space-x-2">
                    <tab.icon className="w-4 h-4" />
                    <span>{tab.name}</span>
                  </div>
                </Tab>
              ))}
            </div>
          </Tab.List>
          <Tab.Panels>
            {tabs.map((tab, idx) => (
              <Tab.Panel
                key={idx}
                className="card"
              >
                {tab.component}
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>
      </div>

      {/* 编辑策略模态框 */}
      <EditStrategyModal
        strategy={data?.strategy || null}
        isOpen={showEditStrategyModal}
        onClose={() => setShowEditStrategyModal(false)}
        onSuccess={handleEditStrategySuccess}
      />

      {/* 编辑策略逻辑模态框 */}
      <EditStrategyLogicModal
        strategy={data?.strategy || null}
        isOpen={showEditLogicModal}
        onClose={() => setShowEditLogicModal(false)}
        onSuccess={handleEditLogicSuccess}
      />

      {/* 删除确认对话框 */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="删除策略"
        message={`确定要删除策略 "${data?.strategy?.name}" 吗？此操作将永久删除该策略及其所有相关数据（参数、回测结果、手动测试记录、研究笔记等），且无法恢复。`}
        confirmText="删除"
        cancelText="取消"
        type="danger"
        onConfirm={handleDeleteStrategy}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* 提示信息 */}
      <Toast
        isVisible={!!toast}
        message={toast?.message || ''}
        onClose={() => setToast(null)}
      />
    </div>
  )
}

export default StrategyDetail 